import React from 'react';
import get_bundle from '../../domain/get_bundle';
import User from '../../domain/User';
import Locale from '../../domain/Locale';
import Product from '../../domain/Product';
import SponsorDomain from '../../domain/Sponsor';
import Color from '../../domain/Color';
import Order from '../../domain/Order';
import Layout from '../../components/Layout';
import Sponsor from '../../components/Sponsor';
import Alert from '../../components/Alert';
import ProductCustomPayments from '../../components/ProductCustomPayments';
import ProductPayments from '../../components/ProductPayments';
import MarkdownComponent from '../../components/MarkdownComponent';
import StdTable from '../../components/StdTable';
import DefaultUi from '../../components/DefaultUi';
import Conditions from '../../components/Conditions';
import Policy from '../../components/Policy';
import CouponShipping from '../../components/CouponShipping';
import UserHeader from '../../components/header/User';
import { t, props } from 'tcomb-react';

@props({
  locale: Locale,
  product: Product,
  user: User,
  sponsor: t.maybe(SponsorDomain),
  order: t.maybe(Order),
  themeBtnColor: t.maybe(Color),
  themeBgColor: t.maybe(Color),
  themeBgFontColor: t.maybe(Color),
  themeBtnFontColor: t.maybe(Color),
  message: t.maybe(t.ReactElement),
  goBuildOrder: t.Func,
  doApproveOrder: t.Func,
  goProduct: t.Func,
  goAccount: t.Func,
  goPurchases: t.Func,
  goChangePassword: t.Func,
  doLogout: t.Func,
  doAcceptCookies: t.Func,
  partners: t.maybe(t.Obj)
})
export default class ApproveOrder extends React.Component {

  getBgColor() {
    if (!this.props.sponsor) {
      return null;
    }
    return this.props.sponsor.bg_color;
  }

  getColor() {
    if (!this.props.sponsor) {
      return null;
    }
    return this.props.sponsor.color;
  }

  getHeader() {
    return (
      <UserHeader
        locale={this.props.locale}
        goAccount={this.props.goAccount}
        goPurchases={this.props.goPurchases}
        goChangePassword={this.props.goChangePassword}
        doLogout={this.props.doLogout}
        goProduct={this.props.goProduct}
        user={this.props.user}
        color={this.getColor()}
        themeBtnColor={this.props.themeBtnColor}
        themeBtnFontColor={this.props.themeBtnFontColor} />);
  }

  getSponsor() {
    const options = {
      showTutorial: false
    };
    return (<Sponsor sponsor={this.props.sponsor} locale={this.props.locale} options={options} />);
  }

  getShipping() {
    const { locale, product, order } = this.props;
    if (product.is_coupon) {
      return (
        <CouponShipping
          locale={locale}
          currency={product.currency} />
      );
    }
    return (
      <div>
        <div>{product.format_amount(locale, order.domicile.shipping.cost)}</div>
        <MarkdownComponent>{order.domicile.shipping.description}</MarkdownComponent>
      </div>
    );
  }

  getSummary() {
    const { locale, product } = this.props;
    const order_bundle = get_bundle(locale).order;

    const data = [
      [order_bundle.summary.quantity, '1'],
      [order_bundle.summary.list_price, product.format_amount(locale, product.list_price)],
      [order_bundle.summary.price, product.format_amount(locale, product.runtime.price)]
    ];
    return <StdTable>{data}</StdTable>;
  }

  getBonusMessage() {
    const { locale, product } = this.props;
    if (!product.runtime.bonus || product.bonus_not_supported) {
      return null;
    }
    return <Alert type='warning'>{get_bundle(locale)['template_message']['approve_order.bonus_warning']}</Alert>;
  }

  doApproveOrder = (evt) => {
    evt.preventDefault();
    const value = this.refs.PrivacyForm.getValue();
    if (value) {
      this.props.doApproveOrder(this.props.order);
    }
  }

  goBuildOrder = (evt) => {
    evt.preventDefault();
    this.props.goBuildOrder(this.props.order);
  }

  getPayment() {
    const {locale, product} = this.props;
    return (
      <div>
        <h3>{get_bundle(locale).product.payment}</h3>
        <ProductCustomPayments locale={locale} customPayments={product.custom_payments} />
        <ProductPayments locale={locale} payments={product.payments} />
      </div>
    );
  }

  getDomicilieRows(domicile) {
    const bundle = get_bundle(this.props.locale).order.domicile;
    return [
      [bundle.name, domicile.name],
      [bundle.surname, domicile.surname],
      [bundle.address, domicile.address],
      [bundle.co, domicile.co],
      [bundle.town, domicile.town],
      [bundle.district, domicile.district],
      [bundle.zip, domicile.zip],
      [bundle.country, this.props.locale.display_country(domicile.shipping.country)],
      [bundle.email, domicile.email],
      [bundle.mobile, domicile.mobile],
      [bundle.alt_email, domicile.alt_email],
      [bundle.alt_mobile, domicile.alt_mobile]
    ];
  }

  getCouponRows(coupon) {
    const bundle = get_bundle(this.props.locale).order.coupon;
    return [
      [bundle.name, coupon.name],
      [bundle.surname, coupon.surname],
      [bundle.email, coupon.email],
      [bundle.mobile, coupon.mobile]
    ];
  }

  getContact() {
    const bundle = get_bundle(this.props.locale).order;
    if (this.props.order.domicile) {
      return (
        <div>
          <h3>{bundle.domicile.title}</h3>
          <StdTable>{this.getDomicilieRows(this.props.order.domicile)}</StdTable>
        </div>
        );
      }
      else if (this.props.order.coupon) {
      return (
        <div>
          <h3>{bundle.coupon.title}</h3>
          <StdTable>{this.getCouponRows(this.props.order.coupon)}</StdTable>
        </div>
      );
    }
  }

  getInvoice() {
    if (!this.props.order.invoice) {
      return null;
    }
    return (
      <div>
        <h3>{get_bundle(this.props.locale).order.invoice.title}</h3>
        <StdTable>{this.getInvoiceRows(this.props.order.invoice)}</StdTable>
      </div>
    );
  }

  getInvoiceRows(invoice) {
    const bundle = get_bundle(this.props.locale).order;
    return [
      [bundle.invoice.name, invoice.name],
      [bundle.invoice.vat, invoice.vat],
      [bundle.invoice.address, invoice.address],
      [bundle.invoice.town, invoice.town],
      [bundle.invoice.district, invoice.district],
      [bundle.invoice.zip, invoice.zip]
    ];
  }

  getTypeForm() {

    const BoolTrue = t.subtype(t.Bool, (x) => x);

    let type = {
      conditions: BoolTrue,
      policy: BoolTrue
    };

    if (this.props.product.hasPrivacy()) {
      type.privacy = BoolTrue;
    }

    return t.struct(type, 'PrivacyForm');
  }

  getOptionsForm() {
    const locale = this.props.locale;

    const getErrorMessage =  () => {
      const bundle = locale.language_bundle();
      return bundle.validation.any;
    };
    return {
      config: {horizontal: {
          md: [3, 9],
          sm: [6, 6]
        }
      },
      fields: {
        conditions: {
          label: (<Conditions locale={locale} />),
          error: getErrorMessage()
        },
        policy: {
          label: (<Policy locale={locale} />),
          error: getErrorMessage()
        },
        privacy: {
          label: <a href='#' id='go_product_privacy' onClick={this.productPrivacyToggle}>{get_bundle(locale).approve_order.privacy.product}</a>,
          error: getErrorMessage()
        }
      }
    };
  }

  productPrivacyToggle() {
    $('#product_privacy').toggle();
  }

  getPrivacyForm() {
    return (
      <div className='form-horizontal'>
        <form>
          <t.form.Form
            ref="PrivacyForm"
            type={this.getTypeForm()}
            options={this.getOptionsForm()} />
        </form>
        {this.getProductPrivacy()}
      </div>
    );
  }

  getProductPrivacy() {
    if (!this.props.product.hasPrivacy()) {
      return null;
    }
    return (<div id='product_privacy' className='box light large-hide'>{this.props.product.privacy}</div>);
  }

  render() {
    const { locale, product } = this.props;

    const bundle = get_bundle(locale).approve_order;
    const order_bundle = get_bundle(locale).order;
    const btnStyle = {
      backgroundColor: this.props.themeBtnColor,
      color: this.props.themeBtnFontColor
    };
    return (
      <Layout header={this.getHeader()} partners={this.props.partners}  sponsor={this.getSponsor()} bgColor={this.getBgColor()} locale={locale} doAcceptCookies={this.props.doAcceptCookies}>
        <DefaultUi id='approve_order' title={bundle.title}  themeBgColor={this.props.themeBgColor} themeBgFontColor={this.props.themeBgFontColor}>
          <div className='cols'>
            <div className='large-100'>
              <div className='cell'>
                <a id='go_build_order' onClick={this.goBuildOrder} className='back' href='#'>{bundle.go_build_order}</a>
              </div>
            </div>
            <div className='large-40 medium-hide'>
              <div className='cell'>
                <img className='product-image' src={product.image}></img>
                {this.getPayment()}
              </div>
            </div>
            <div className='large-60 medium-100'>
              <div className='cell'>
                {this.props.message}
                <h2>{product.name}</h2>
                <h3>{order_bundle.summary.title}</h3>
                {this.getSummary()}
                <h3>{order_bundle.shippings.title}</h3>
                {this.getShipping()}
                <div className='large-hide medium-show'>
                  {this.getPayment()}
                </div>
                {this.getContact()}
                {this.getInvoice()}
                <h3>{bundle.privacy.title}</h3>
                <p>{bundle.privacy.disclaimer}</p>
                {this.getPrivacyForm()}
                {this.getBonusMessage()}
                <button id='do_approve_order' className='large-left-30p small-left-0p' onClick={this.doApproveOrder} style={btnStyle}>{product.getBuyButtonLabel(locale)}</button>
              </div>
            </div>
          </div>
        </DefaultUi>
      </Layout>
    );
  }
}


/** WEBPACK FOOTER **
 ** C:/repository/madai/v6/client/lib/web/product/route/ApproveOrder.js
 **/